import React from "react";
import { FormControl, Typography, Box, FormGroup, FormControlLabel, Switch, FormHelperText } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { getEmailSchedulerDefinitions, getRuleEditProperties } from "store/features/emailScheduler/definition";
import { useSelectOptions } from "hooks";
import { CreateEmailSchedulerRuleForm, orders } from "validation";
import { Select, TextField } from "components";
import { initialStatuses } from "api/factories/emailScheduler.factory";

/**
 * General section wrapper
 */
export const GeneralSection = () => {
    const { control, errors } = useFormContext<CreateEmailSchedulerRuleForm>();
    const { deadlines } = useSelector(getEmailSchedulerDefinitions);
    const deadlinesDetails = useSelectOptions(deadlines);
    const { isEdited, isCopy } = useSelector(getRuleEditProperties);
    const order = useWatch({
        control,
        name: "general.order",
    });

    return (
        <>
            <Typography component="div" variant="h6">
                <Box pb={2}>{isEdited && !isCopy ? "Edit rule" : "Create new rule"}</Box>
            </Typography>
            <Box display="flex" alignItems="center" pb={4}>
                <Box flex={0.5}>
                    <FormControl fullWidth={true}>
                        <Controller
                            name="general.name"
                            control={control}
                            as={
                                <TextField
                                    label="Rule name"
                                    error={!!errors.general?.name}
                                    helperText={errors.general?.name?.message}
                                />
                            }
                        />
                    </FormControl>
                </Box>
            </Box>
            <Typography component="div" variant="subtitle2">
                <Box fontWeight="fontWeightBold">General settings</Box>
            </Typography>
            <Box display="flex" alignItems="flex-end" pt={1} mb={2}>
                <Box display="flex" flex={0.5}>
                    <Select
                        name="general.deadlineMaterial"
                        title="Relative to"
                        options={deadlinesDetails}
                        error={errors.general?.deadlineMaterial}
                    />

                    <Select
                        name="general.order"
                        defaultValue={order}
                        onChange={(onChange, event) => onChange(event.target.value)}
                        title="Level"
                        options={orders}
                        error={errors.general?.order}
                        disableMargin
                    />
                </Box>
            </Box>
            <Box display="flex" alignItems="flex-end" pt={1} mb={1}>
                <FormControl component="fieldset">
                    <FormGroup row={true}>
                        <Controller
                            name="general.weekends"
                            control={control}
                            render={({ onChange, value }) => {
                                return (
                                    <FormControlLabel
                                        label="Exclude weekends"
                                        labelPlacement="end"
                                        control={
                                            <Switch
                                                color="primary"
                                                checked={value}
                                                onChange={(event, checked) => onChange(checked)}
                                            />
                                        }
                                    />
                                );
                            }}
                        />
                        <Controller
                            name="general.active"
                            control={control}
                            render={({ onChange, value }) => {
                                return (
                                    <FormControlLabel
                                        label="Active"
                                        labelPlacement="end"
                                        control={
                                            <Switch
                                                color="primary"
                                                checked={value}
                                                onChange={(event, checked) => onChange(checked)}
                                            />
                                        }
                                    />
                                );
                            }}
                        />
                    </FormGroup>
                </FormControl>
            </Box>
            <Box display="flex" pt={1} mb={4}>
                <Box display="flex" flex={0.5}>
                    <FormControl fullWidth={true}>
                        <Controller
                            name="general.initialStatuses"
                            defaultValue={[]}
                            control={control}
                            render={({ onChange, value }) => {
                                return (
                                    <Autocomplete
                                        value={value}
                                        onChange={(event, val) => onChange(val)}
                                        fullWidth={true}
                                        multiple={true}
                                        filterSelectedOptions={true}
                                        options={initialStatuses}
                                        getOptionLabel={(option) => option.name}
                                        getOptionSelected={(option, value) => option.name === value.name}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={!!errors.general?.initialStatuses}
                                                label="Set initial statuses"
                                                placeholder="Initial statuses..."
                                            />
                                        )}
                                    />
                                );
                            }}
                        />
                        {errors.general?.initialStatuses && (
                            <FormHelperText error={true}>
                                {/*TODO: temporary walk-around, for some reasons resolver is NOT typing property as object*/}
                                {/*eslint-disable-next-line*/}
                                {errors.general?.initialStatuses?.["message" as any]}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </Box>
        </>
    );
};
